import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getAllConfigurations } from '@/services/adeApi';

export interface AbmConnection {
  id: string;
  name: string;
  organization_id: string;
  customer_id: string | null;
  client_id: string;
  client_secret: string;
  access_token: string | null;
  refresh_token: string | null;
  server_token: string | null;
  token_expires_at: string | null;
  is_active: boolean | null;
  last_sync_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const useAbmConnections = (customerId?: string) => {
  const [connections, setConnections] = useState<AbmConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchConnections();
  }, [customerId]);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      setError(null);
      const configs = await getAllConfigurations();
      // Map ADE configs to AbmConnection shape
      const mapped: AbmConnection[] = (configs || []).map((c: any) => ({
        id: c.id || c.configId || '',
        name: c.name || c.orgName || '',
        organization_id: c.orgId || '',
        customer_id: customerId || null,
        client_id: '',
        client_secret: '',
        access_token: null,
        refresh_token: null,
        server_token: null,
        token_expires_at: null,
        is_active: true,
        last_sync_at: null,
        created_at: null,
        updated_at: null,
      }));
      setConnections(customerId ? mapped.filter(c => c.customer_id === customerId) : mapped);
    } catch (err) {
      console.error('Error fetching ABM connections:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch ABM connections'));
      toast({ title: 'Error', description: 'Failed to load Apple Business Manager connections', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const createConnection = async (connectionData: any) => {
    toast({ title: 'Info', description: 'Create connection via ADE API - coming soon' });
    await fetchConnections();
    return connectionData;
  };

  const updateConnection = async (id: string, updates: Partial<AbmConnection>) => {
    toast({ title: 'Info', description: 'Update connection via ADE API - coming soon' });
    await fetchConnections();
  };

  const deleteConnection = async (id: string) => {
    toast({ title: 'Info', description: 'Delete connection via ADE API - coming soon' });
    await fetchConnections();
  };

  const testConnection = async (id: string) => {
    toast({ title: 'Info', description: 'Test connection via ADE API - coming soon' });
    return {};
  };

  return {
    connections,
    loading,
    error,
    fetchConnections,
    createConnection,
    updateConnection,
    deleteConnection,
    testConnection,
    getActiveConnections: () => connections.filter(conn => conn.is_active),
    getConnectionsByCustomer: (cid: string) => connections.filter(conn => conn.customer_id === cid),
  };
};
